import React, { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  PostTeeSheetNotificationTemplate,
  TTeesheetNotificationType,
} from "api/rpc/2024-04/facilityAdmin/teesheet/notification";

import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import { Select } from "components/select/";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import TextEditor from "components/textEditor/textEditor";

interface IValidatedField<T> {
  value: T;
  isValid: boolean;
  isDirty: boolean;
}

interface INotificationUserTypeOption {
  label: string;
  value: TTeesheetNotificationType;
}

interface INotificationTemplateNewState {
  userType: IValidatedField<TTeesheetNotificationType | undefined>;
  subject: IValidatedField<string>;
  content: IValidatedField<string>;
}

export default function TeeSheetNotificationTemplateNew() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const notificationUserTypeOptions: INotificationUserTypeOption[] = useMemo(() => {
    return [
      {
        label: t("Internal"),
        value: "admin",
      },
      {
        label: t("External"),
        value: "customer",
      },
    ];
  }, []);

  const [state, setState] = useState<INotificationTemplateNewState>({
    userType: { value: undefined, isValid: false, isDirty: false },
    subject: { value: "", isValid: false, isDirty: false },
    content: { value: "", isValid: true, isDirty: false },
  });

  async function postTemplate() {
    setState(previousState => ({
      ...previousState,
      userType: { ...previousState.userType, isDirty: true },
      subject: { ...previousState.subject, isDirty: true },
      content: { ...previousState.content, isDirty: true },
    }));

    if (!state.userType.isValid || !state.subject.isValid) {
      return;
    }

    const response = await PostTeeSheetNotificationTemplate(
      {
        subject: state.subject.value,
        user_type: state.userType.value,
        content: state.content.value,
      },
      true,
    );

    if (response.status !== StatusCode.OK) {
      dispatch(showError("Error creating notification template"));
      return;
    }

    history.push("/admin/settings/tee-sheet/notification-templates");
  }

  const primaryAction = {
    content: t("Create"),
    action: postTemplate,
  };

  return (
    <Page
      title={t("New Notification Template")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to notification templates"),
          url: "/admin/settings/tee-sheet/notification-templates",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Select
                label={t("User Type")}
                onChange={value =>
                  setState(previousState => ({
                    ...previousState,
                    userType: { ...previousState.userType, value, isValid: true, isDirty: true },
                  }))
                }
                defaultValue={state.userType.value}
                error={!state.userType.isValid && state.userType.isDirty}
                helpText="Selecting 'Internal' will show this notification on your internal tee sheet. Selecting 'External' will show this notification on the customer tee sheet."
              >
                {notificationUserTypeOptions.map((option, optionIndex: number) => {
                  return (
                    <Select.Option key={optionIndex} value={option.value} name={option.label}>
                      {option.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={state.subject.value}
                label={t("Header")}
                onChange={event =>
                  setState(previousState => ({
                    ...previousState,
                    subject: {
                      ...previousState.subject,
                      value: event.target.value,
                      isValid: event.target.value !== "",
                      isDirty: true,
                    },
                  }))
                }
                error={!state.subject.isValid && state.subject.isDirty}
                placeholder={t("Header")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextEditor
                markdownText={state.content.value || ""}
                markdownTextOnChange={newContent =>
                  setState(previousState => ({
                    ...previousState,
                    content: {
                      ...previousState.content,
                      value: newContent,
                      isValid: true,
                    },
                  }))
                }
                label={t("Contents")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}

